<template>
  <DetailLayoutFullWidth>
    <template slot="header">
      <base-header-financial-dashboard
        :title="$t('financial_dashboard.settlement')"
        @change="handleChangeEvent"
        @refresh="refreshPage"
        @print="printDashboard"
      />
    </template>
    <template slot="main">
      <loading :opacity="darkMode ? 0 : 0.5" :active="isGeneralLoading" :is-full-page="false" :z-index="10" color="var(--primary-DEFAULT)"></loading>
      <div class="print-area mt-2 md:mt-6 space-y-5">
        <div class="flex flex-wrap gap-4">
          <single-panel class="w-full flex-1 min-w-max" :header="$t('tipos.tax_category.charged')" :content="impuestoRepercutido" :footer="$t('financial_dashboard.about_issued_invoices')">
            <template slot="header">
              <base-dot-icon class="text-primary-800 dark:text-primary-400" :style="{ color: taxChargedColor }"/>
            </template>
          </single-panel>
          <single-panel class="content-red-500 w-full flex-1 min-w-max" :header="$t('tipos.tax_category.borne')" :content="impuestoSoportado" :footer="$t('financial_dashboard.about_received_invoices')">
            <template slot="header">
                <base-dot-icon class="text-red-500 dark:text-red-400" :style="{ color: taxBorneColor }"/>
              </template>
          </single-panel>
          <single-panel class="w-full flex-1 min-w-max" :class="taxResultColor" :header="$t('financial_dashboard.settlement_result')" :content="resultLiquidacion" :footer="$t('financial_dashboard.be_paid_to_hacienda')">
            <template slot="header">
              <base-dot-icon :style="{ color: balanceColor, borderWidth: this.isNegativeTax ? '1px' : '0', borderColor: this.isNegativeTax ? '#B91C1C' : '' } "/>
            </template>
          </single-panel>
          <!-- <single-panel class="w-full" header="Próxima Presentación" content="20 Abr" footer="Modelo 303 - 1T 2026"/> -->
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <BasePanel>
            <template slot="header">
              <div class="flex flex-col gap-2 md:flex-row xl:flex-col md:justify-between">
                <h3>{{ $t('tipos.tax_category.charged') }} vs {{ $t('tipos.tax_category.borne') }} ({{ dataPeriod }})</h3>
                <div v-if="false" class="flex gap-5 text-secondary-500 dark:text-darkblue-100 text-sm">
                  <span 
                    @click="toggleDatasetChart(refPanelChart,1)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(refPanelChart, 1) ? 'line-through' : 'none' }">
                    <base-dot-icon :style="{ color: taxChargedColor }" />
                    <span>{{ $t('tipos.tax_category.charged') }}</span>
                  </span>
                  <span 
                    @click="toggleDatasetChart(refPanelChart,2)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(refPanelChart, 2) ? 'line-through' : 'none' }">
                    <base-dot-icon :style="{ color: taxBorneColor }" />
                    <span>{{ $t('tipos.tax_category.borne') }}</span>
                  </span>
                  <span 
                    @click="toggleDatasetChart(refPanelChart,0)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(refPanelChart, 0) ? 'line-through' : 'none' }">
                    <base-dot-icon :style="{ color: balanceColor }" />
                    <span>{{ $t('app.balance') }}</span>
                  </span>
                </div>
              </div>
            </template>
            <template slot="content">
              <div class="w-full overflow-hidden">
                <Chart id="chart-panel"
                  :ref="refPanelChart"
                  type="bar"
                  :show-legend="true"
                  :expand="true"
                  :charData="chartBarDataStatus"
                  :parse-format="formatValueDate"
                  :x-axis-value-format="formatChartDate"
                  y-axis-value-format="compact"
                  tooltip-y-value-format="currency"
                  y-axis-suffix="€"
                />
              </div>
            </template>
          </BasePanel>
          <BasePanel id="break_table">
            <h3 class="font-semibold mb-3 flex items-center">
              {{$t('financial_dashboard.breakdown_by_tax_rates')}} {{ dateFormat ? `(${dateFormat})` : '' }}
            </h3>
            <div class="overflow-x-auto">
              <el-table ref="summaryTable" class="text-primary-900 border-primary-100 dark:text-primary-500 dark:border-darkblue-50 min-w-max shadow-sm"
                :data="dataTable" :cell-class-name="getCellClassName" show-summary :summary-method="getTotal" style="table-layout:auto">
                <el-table-column
                  prop="tipoTable"
                  min-width="90"
                  :label="$t('imports.type')">
                </el-table-column>
                <el-table-column
                  v-for="(label, prop) in tableColumns"
                  :key="prop"
                  :prop="prop"
                  :min-width="maxColumnWidth[prop]"
                  :formatter="formatCellValue"
                  :label="label">
                </el-table-column>
              </el-table>
            </div>
          </BasePanel>
        </div>
      </div>
    </template>
  </DetailLayoutFullWidth>
</template>
    
<script>
import { i18n } from '@/i18n'
import _ from 'lodash'
import moment from 'moment'
import Chart from '@/components/charts/Chart.vue'
import colorsMixin from '@/mixins/colorsMixin'
import Loading from 'vue-loading-overlay'
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import SinglePanel from '@/components/SinglePanel.vue'

import { getDashboardFinancieroImpuestos } from '@/services/dashboard/getDashboardFinancieroImpuestos'
    
export default {
  name: 'financial-dashboard',
  mixins: [colorsMixin],
  components: {
    DetailLayoutFullWidth,
    SinglePanel,
    Loading,
    Chart
  },
  props: {},
  data () {
    return {
      financialTaxes: [],
      chartBarDataStatus: null,
      refPanelChart: 'panelChart',
      tableColumns: {
        BaseRepercutido: i18n.t('financial_dashboard.base_rep'),
        ImpuestoRepercutido: i18n.t('tipos.tax_category.charged'),
        BaseSoportado: i18n.t('financial_dashboard.base_sop'),
        ImpuestoSoportado: i18n.t('tipos.tax_category.borne'),
        Total: i18n.t('financial_dashboard.difference')
      },
      tableFontStyle: {
        font: '',
        letterSpacing: 0,
        paddingX: 0
      },
      printTableDefaults: {
        iframeWidth: '768px',
        horizontalThreshold: '700px',
        columnWidth: '220px',
        kpiCardWidth: '1400px'
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    clientId () {
      return this.$store.getters['dashboard/getSelectedClient'] || null
    },
    disableSelect () {
      return !this.cifEmpresa || this.isGeneralLoading 
    },
    activePeriod () {
      return this.$store.getters['financialdashboard/activePeriod']
    },
    cifEmpresa () {
      return this.$store.getters['financialdashboard/cifEmpresa']
    },
    valueDate () {
      return this.$store.getters['financialdashboard/valueDate']
    },
    currentFormat () {
      return this.$store.getters['financialdashboard/currentFormat']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    maxColumnWidth () {
      const max = {}
      Object.entries(this.tableColumns).forEach(([prop, labelKey]) => {
        max[prop] = this.textWidthPx(labelKey)
      })
      const dataTable = _.clone(this.dataTable)
      dataTable.push(this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral ?? {})
      dataTable.forEach(row => {
        Object.keys(max).forEach(key => {
          const formatted = i18n.n(row[key] ?? 0, 'currency')
          max[key] = Math.max(max[key], this.textWidthPx(formatted))
        })
      })
      return max
    },
    dataPerPeriodImpuesto () {
      let picker = this.valueDate 
      if (this.activePeriod ==='semana') {
        picker = moment(picker).format('[Semana] W')
      }
      if (!picker) {
        return this.financialTaxes.at(-1)
      }
      return this.financialTaxes.find((p) => p.Fecha === picker) 
    },
    activePeriods: function () {
      return this.$store.getters['config/ACTIVE_PERIODS']
    },
    dataPeriod () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.timePeriod ?? this.activePeriod
    },
    isDatasetHidden () {
      return this.$store.getters['financialdashboard/isDatasetHidden']
    },
    formatChartDate () {
      return this.currentFormat.i18nFormat || this.currentFormat.momentDate
    },
    formatValueDate () {
      return this.currentFormat.momentValueDate
    },
    dateFormat () {
      const testDate = moment(this.valueDate, this.formatValueDate, true)
      const value = testDate.isValid() ? testDate.valueOf() : this.valueDate

      const numericValue = Number(value)
      const isNumericValue = Number.isFinite(numericValue)
      let formattedValue = value
      if (isNumericValue) {
        if (i18n.ne(this.formatChartDate)) {
          formattedValue = i18n.n(numericValue, this.formatChartDate)
        } else if (i18n.de(this.formatChartDate)) {
          formattedValue = _.capitalize(i18n.d(numericValue, this.formatChartDate))
        } else if (this.formatChartDate) {
          formattedValue = _.capitalize(moment(numericValue).locale(i18n.locale).format(this.formatChartDate))
        } else {
          formattedValue = i18n.n(numericValue, 'decimal')
        }
      }
      return formattedValue || this.valueDate
    },
    dataTable () {
      const table=[]
      if (this.dataPerPeriodImpuesto?.ImpuestoDesgloseDashboardPorcentaje) {
        for (const data of this.dataPerPeriodImpuesto.ImpuestoDesgloseDashboardPorcentaje) {
          data.TipoF = this.tiposCodigoImpuesto.find((obj) => obj.id===data.Tipo)?.short?? data.Tipo
          data.PorcentajeF = i18n.n(data.Porcentaje, 'percent_decimal')
          data.tipoTable = `${data.TipoF} ${data.PorcentajeF}`
          table.push(data)
        }
      }
      return table
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    impuestoRepercutido () {
      return `${i18n.n(this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral.ImpuestoRepercutido ?? 0, 'currency')} €`
    },
    impuestoSoportado () {
      return `${i18n.n(this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral.ImpuestoSoportado ?? 0, 'currency')} €`
    },
    resultLiquidacion () {
      return `${i18n.n(this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral.Total ?? 0, 'currency')} €`
    },
    taxBorneColor () {
      return this.tiposCategoriaImpuesto.find(c => c.id === 1)?.color ?? '#000000'
    },
    taxChargedColor () {
      return this.tiposCategoriaImpuesto.find(c => c.id === 2)?.color ?? '#000000'
    },
    taxResultColor () {
      return this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral.Total < 0 ? 'content-red-700' : 'content-yellow-500'
    },
    isNegativeTax () {
      return this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral.Total < 0
    },
    balanceColor () {
      return '#FFDA9E'
    },
    enabledDates () {
      return this.financialTaxes.map(df => df.Fecha)
    },
    tiposCodigoImpuesto: function () {
      return this.$store.getters['config/TIPOS_CODIGO_IMPUESTO']
    },
    tiposCategoriaImpuesto: function () {
      return this.$store.getters['config/TIPOS_CATEGORIA_IMPUESTOS']
    }
  },
  created () {
    // No queremos reactividad
    this._measureCanvas = null
  },
  mounted: async function () {
    this.$nextTick(this.readTableFont)
  },
  methods: {
    async getDashboardFinancieroImpuestos (period=this.activePeriod) {
      try {
        this.$store.commit('setIsLoading', true)
        this.financialTaxes=[]
        const unitTime = this.$store.getters['financialdashboard/momentTimeUnit']
        const params = {
          idCifEmpresa: this.cifEmpresa?.IdCIFEmpresa ?? null,
          fechaFiltro: period,
          FechaFin: moment(this.valueDate, this.currentFormat.momentValueDate).startOf('day').endOf(unitTime).format('YYYY-MM-DDT[00:00:00]'),
        }
        const dataDashboard = await getDashboardFinancieroImpuestos(params)
        
        if (!dataDashboard.blnError) {
          for (const data of dataDashboard.Response) {
            this.financialTaxes.push(data)
          }
        } else {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', dataDashboard))
        }
        setTimeout(() => {
          this.loadChartData()
        }, 100)
      } catch (error) {
        console.log('CustomLog: error', error)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    loadChartData () {
      const data = this.financialTaxes ?? []
      this.chartBarDataStatus = {
        labels: [],
        datasets: [
          {
            type: 'line',
            label: i18n.t('app.balance'),
            data: [],
            borderColor: this.balanceColor,
            backgroundColor: this.balanceColor,
            borderWidth: 2,
            pointBorderWidth: 1,
            pointBorderColor: [],
            fill: false,
          },
          {
            label: i18n.t('tipos.tax_category.charged'),
            data: [],
            backgroundColor: this.tiposCategoriaImpuesto.find(c => c.id === 2)?.color ?? '#000000',
          },
          {
            label: i18n.t('tipos.tax_category.borne'),
            data: [],
            backgroundColor: this.tiposCategoriaImpuesto.find(c => c.id === 1)?.color ?? '#000000',
          }
        ]
      }

      data.forEach(element => {
        this.chartBarDataStatus.labels.push(element.Fecha)
        const repercutido = element?.ImpuestosDesgloseGeneral?.ImpuestoRepercutido ?? 0
        const soportado = element?.ImpuestosDesgloseGeneral?.ImpuestoSoportado ?? 0
        const resultado = repercutido - soportado
        this.chartBarDataStatus.datasets[1].data.push(repercutido)
        this.chartBarDataStatus.datasets[2].data.push(soportado)
        this.chartBarDataStatus.datasets[0].pointBorderColor.push(resultado < 0 ? '#B91C1C' : this.balanceColor)
        this.chartBarDataStatus.datasets[0].data.push(resultado)
      })
    },
    formatCellValue (row, column, cellValue, index) {
      return i18n.n(cellValue ?? 0, 'currency')
    },
    getCellClassName ({ row, column, rowIndex, columnIndex }) {
      const value = parseFloat(row[column.property])
      if (!isNaN(value) && value < 0) {
        return 'text-red-500'
      }
      return ''
    },
    getTotal ({ columns }) {
      const resumen = this.dataPerPeriodImpuesto?.ImpuestosDesgloseGeneral
      const getClassName = value => !isNaN(value) && value < 0 ? 'text-red-700' : ''
      return [
        i18n.t('app.total'),
        ...columns.slice(1).map(col => parseFloat(resumen?.[col.property] ?? 0))
          .map(v => this.$createElement('span', { class: getClassName(v) }, i18n.n(v, 'currency')))
      ]
    },
    readTableFont () {
      const cell = this.$refs.summaryTable?.$el?.querySelector('.el-table__cell .cell')
      if (!cell) return
      const cs = window.getComputedStyle(cell)
      this.tableFontStyle = {
        font: cs.font,
        letterSpacing: parseFloat(cs.letterSpacing) || 0,
        paddingX: (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
      }
    },
    textWidthPx (text) {
      this._measureCanvas = this._measureCanvas || document.createElement('canvas')
      const ctx = this._measureCanvas.getContext('2d')
      const { font, letterSpacing, paddingX } = this.tableFontStyle
      ctx.font = font
      const t = String(text ?? '')
      const base = ctx.measureText(t).width
      const spacing = letterSpacing * Math.max(t.length - 1, 0)
      return Math.ceil(base + spacing + paddingX + 5)
    },
    handleChangeEvent ({ field, value, refresh }) {
      if (refresh) {
        if (field !== 'valueDate' || !this.enabledDates.includes(value)) {
          this.refreshPage()
        } else {
          setTimeout(() => {
            // this.loadChartData()
          }, 100)
        }
      }
    },
    async refreshPage () {
      this.$store.commit('financialdashboard/resetHiddenDatasets')
      await this.getDashboardFinancieroImpuestos()
    },
    toggleDatasetChart (refChart, datasetIndex) {
      this.$store.commit('financialdashboard/toggleHiddenDataset', { refChart, datasetIndex })
      this.$refs[refChart].toggleDatasetVisibility(datasetIndex)
    },
    printDashboard () {
      const printArea = this.$el.querySelector('.print-area')
      if (!printArea) return

      const oldIframe = document.getElementById('printeriframe')
      if (oldIframe) oldIframe.remove()

      const clone = printArea.cloneNode(true)
      const originalCanvas = printArea.querySelectorAll('canvas')

      clone.querySelectorAll('canvas').forEach((cloneCanvas, index) => {
        if (originalCanvas[index]) {
          const chartRef = this.$refs[this.refPanelChart]
          const sourceCanvas = chartRef?.trimCanvas() || originalCanvas[index]
          const img = document.createElement('img')
          img.src = sourceCanvas.toDataURL('image/png')
          img.style.width = this.isLower1024 ? '65%' : '100%'
          img.style.justifySelf = 'center'
          img.style.alignSelf = 'center'
          cloneCanvas.parentNode.replaceChild(img, cloneCanvas)
        }
      })

      clone.querySelectorAll('.el-table__body-wrapper, .el-table__header-wrapper').forEach(el => {
        el.style.overflow = 'visible'
      })

      const iframe = document.createElement('iframe')
      iframe.setAttribute('id', 'printeriframe')
      iframe.setAttribute('width', this.printTableDefaults.iframeWidth)
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      const allStyles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
        .map(el => el.outerHTML).join('\n')

      let horizontal = '@page tabla {size: A4 ; margin: 10mm;}'
      let breakStyle = ''
      const tableWidth = window.getComputedStyle(document.querySelector('.el-table')).width
      if (tableWidth >= this.printTableDefaults.horizontalThreshold) {
        horizontal = '@page tabla {size: A4 landscape; margin: 10mm;}'
        breakStyle = '#break_table {page: tabla;break-before: always;page-break-before: always;}'
        
        const table = clone.getElementsByClassName('el-table')[0]
        const basePanelTable = clone.getElementsByClassName('kpi-card')[clone.getElementsByClassName('kpi-card').length -1]
        basePanelTable.style.width = this.printTableDefaults.kpiCardWidth
        table.classList.remove('min-w-max')
        table.style.tableLayout = 'auto'
        table.style.minWidth = '0px'
        table.style.width = this.printTableDefaults.kpiCardWidth
  
        if (table) {
          const colGroup = table.querySelectorAll('colgroup')
          for (let j = 0; j < colGroup.length; j++) {
            for (let i = 0; i < colGroup[j].children.length; i++) {
              const element = colGroup[j].children[i]
              element.setAttribute('width', this.printTableDefaults.columnWidth)
              element.style.width = this.printTableDefaults.columnWidth
              element.style.minWidth = '0px'
              element.style.padding = '0px'
            }
          }
        }
      }

      iframeDoc.open()
      iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8">${allStyles}</head><body>${clone.outerHTML}</body></html>`)
      iframeDoc.close()

      const printStyle = iframeDoc.createElement('style')
      printStyle.textContent = `
        @page { 
          size: A4; 
          margin: 10mm;
        }
       ${horizontal}
        @media print {
          .print-area { 
            shadow: none !important; 
          }
          .kpi-card {
              break-inside: avoid;
              page-break-inside: avoid;
          }
          ${breakStyle}
        }
        *, *::before, *::after { 
          box-sizing: border-box; 
        }
        html, body {
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
          overflow: visible;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-area {
          width: 100%;
          padding: 0;
          margin: 0;
        }
        `
      iframeDoc.head.appendChild(printStyle)

      setTimeout(() => {
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
        } catch (e) {
          console.error('Error al imprimir:', e)
        }
        setTimeout(() => iframe.remove(), 1000)
      }, 300)
    },
  }
}
</script>
<style scoped>
.el-table::v-deep .el-table__cell:first-child .cell{
  white-space: normal;
  word-break: break-word;
}
@media print {
  body * {
    visibility: hidden;
  }
  .print-area,.print-area * {
    visibility: visible;
  }
  .print-area {
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
